import { useRef, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { ApiFailure } from '../api'

export default function Login() {
  const { user, loading, ended, signIn } = useAuth()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [rejected, setRejected] = useState(false)
  const [busy, setBusy] = useState(false)
  const passwordRef = useRef<HTMLInputElement>(null)

  if (loading) return <div className="loading">Loading&hellip;</div>
  if (user) return <Navigate to="/" replace />

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await signIn(username, password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not sign in.')
      // A password the browser filled in would otherwise sit there and be
      // resubmitted unchanged on every attempt, so the person never sees that
      // what they are sending is not what they think it is.
      const wrongCredential = err instanceof ApiFailure && err.status === 401
      setRejected(wrongCredential)
      if (wrongCredential) {
        setPassword('')
        // Focus after the re-render: clearing the value disables the submit
        // button, and React moves focus before a synchronous call would stick.
        setTimeout(() => passwordRef.current?.focus(), 0)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={(e) => void handleSubmit(e)}>
        <h1>Sign in</h1>
        <p className="sub">Clinic Patient Record</p>

        {/* Announced, because a failed sign-in moves no focus and repaints
            nothing a screen reader would otherwise notice. */}
        <div role="alert" aria-live="assertive">
          {error && <div className="msg error">{error}</div>}
        </div>
        {ended && !error && (
          <div className="msg ok" role="status">
            {ended === 'expired' ? 'Your session ended. Sign in again.' : 'You have signed out.'}
          </div>
        )}
        {rejected && (
          <p className="hint" style={{ marginBottom: 14 }}>
            The password box has been cleared. If your browser filled it in, that saved password is out
            of date &mdash; type the current one instead.
          </p>
        )}

        <div className="grid">
          <div className="field">
            <label htmlFor="username">Phone number or email</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
            <span className="hint">Staff with an older login can still use their username.</span>
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              ref={passwordRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <button type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </div>

      </form>
    </div>
  )
}
